import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import SelectInput from "@/components/inputs/SelectInput";

const DEFAULT_CURRENCY_VALUE = process.env.NEXT_PUBLIC_DEFAULT_CURRENCY_VALUE;

const ITEM_TEMPLATE =
  "<div class='flex justify-between w-full'>" +
  "<span class='font-semibold'>${code}</span>" +
  "<span class='text-gray-500 dark:text-gray-400'>${description} (${symbol})</span>" +
  "</div>";

/**
 * CurrencySelect — Syncfusion DropDownList pre-wired to the currencies Redux store.
 *
 * Drop-in replacement for the legacy react-select `CurrencySelectBox`.
 * Emits the selected `parameterId` (string) via `onChange`, consistent with
 * the other Syncfusion-based input wrappers.
 *
 * @example
 * <Controller
 *   name="currencyId"
 *   control={control}
 *   render={({ field }) => (
 *     <CurrencySelect
 *       placeholder="Currency"
 *       value={field.value}
 *       onChange={field.onChange}
 *       error={errors.currencyId?.message}
 *     />
 *   )}
 * />
 */
export default function CurrencySelect({
  placeholder = "Currency",
  value,
  onChange,
  error,
  disabled = false,
  className,
  ...props
}) {
  const { currencies: records } = useSelector(state => state.currencies);

  const dataSource = useMemo(() => {
    if (!records?.length) return [];
    return records.map(currency => ({
      text: `${currency.code} — ${currency.name} (${currency.value})`,
      value: currency.parameterId,
      code: currency.code,
      description: currency.name,
      symbol: currency.value,
    }));
  }, [records]);

  return (
    <SelectInput
      placeholder={placeholder}
      dataSource={dataSource}
      value={value ?? DEFAULT_CURRENCY_VALUE}
      onChange={onChange}
      error={error}
      disabled={disabled}
      className={className}
      itemTemplate={ITEM_TEMPLATE}
      {...props}
    />
  );
}

CurrencySelect.propTypes = {
  /** Floating-label / placeholder text */
  placeholder: PropTypes.string,
  /** Controlled value — currency parameterId */
  value: PropTypes.any,
  /** Change handler — receives the selected parameterId */
  onChange: PropTypes.func,
  /** Validation error message */
  error: PropTypes.string,
  /** Fully disabled */
  disabled: PropTypes.bool,
  /** Additional CSS classes for the wrapper */
  className: PropTypes.string,
};

